import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import Bluebird from 'bluebird';
import queryString from 'query-string';
import moment from 'moment';
import { generatePath } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import snackbarHelper from '../../helpers/snackbarHelper';
import * as errorsHelper from '../../helpers/errors';
import * as authService from '../../services/auth';
import * as authActions from '../../actions/auth';
import * as usersActions from '../../actions/users';
import * as userRegistrationsApi from '../../api/userRegistrations';
import { verifyEmail as verifyEmailApi } from '../../api/users';
import routes from '../../routes';
import { store } from '../../store';

// Landing route for the verification email link (${FRONTEND_URL}/verify-email?token=…).
// On native this route is reached via a Universal Link (see services/deepLinks.js);
// on web via a normal browser navigation. Either way we POST the token to flip
// email_verified server-side, then complete the flow from the persisted session.
class VerifyEmail extends React.Component {
  componentDidMount() {
    const { history, location } = this.props;
    const { token } = queryString.parse(location.search);

    if (!token) {
      snackbarHelper.error('Invalid or missing verification link.');

      history.push(routes.login);

      return;
    }

    verifyEmailApi(token)
      .then(() => this.completeVerification())
      .catch((err) => {
        errorsHelper.handleError(err);

        history.push(routes.login);
      });
  }

  completeVerification() {
    const { accessToken, userRegistration, setUserRegistration, history } = this.props;

    // Case 1: mid-registration — signed up in this session, profile not yet created.
    if (authService.isCompleteRegistrationProcessPending() && userRegistration && userRegistration.accessToken) {
      return Bluebird.try(() =>
        userRegistrationsApi.updateUserRegistration(
          userRegistration.data.id,
          { email_verified: true },
          userRegistration.accessToken
        )
      )
        .then(() =>
          setUserRegistration(userRegistration.accessToken, userRegistration.idToken, {
            ...userRegistration.idTokenPayload,
            email_verified: true,
          })
        )
        .then(() => {
          if (userRegistration.data.tos_accepted !== null) {
            return authService.createProfile(userRegistration, history);
          }

          return history.push(generatePath(routes.completeRegistrationNew));
        })
        .catch((err) => errorsHelper.handleError(err));
    }

    // Case 2: an already logged-in user verifying their email.
    if (accessToken) {
      const currentUser = authService.getCurrentlyLoggedInOrImpersonatingUser();

      return Bluebird.try(() =>
        store.dispatch(
          usersActions.updateUser(currentUser.id, { email_verified: moment().format('YYYY-MM-DD HH:mm:ss') })
        )
      )
        .then(() => store.dispatch(usersActions.fetchAndSetLoggedInUser(currentUser.id)))
        .then(() => {
          snackbarHelper.success('Your email address has been verified.');

          history.push(routes.index);
        })
        .catch((err) => errorsHelper.handleError(err));
    }

    // Case 3: no active session in this browser (e.g. link opened on another device).
    // The email is already verified server-side; send them to log in.
    snackbarHelper.success('Your email address has been verified. Please log in.');

    return history.push(routes.login);
  }

  render() {
    return <CircularProgress color="inherit" />;
  }
}

const mapStateToProps = ({ app: { accessToken, userRegistration } }) => ({
  accessToken,
  userRegistration,
});

const mapDispatchToProps = (dispatch) => ({
  setUserRegistration: (accessToken, idToken, idTokenPayload, data) =>
    dispatch(authActions.setUserRegistration(accessToken, idToken, idTokenPayload, data)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(VerifyEmail));
